import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, Users2, Dumbbell, Building2, Heart } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../lib/AuthContext";
import { formatApiErrorDetail } from "../../lib/api";
import { useBrand } from "../../lib/BrandContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import PasswordInput from "../../components/PasswordInput";
import { Label } from "../../components/ui/label";
import {
  MVP_INDUSTRY_LOCKED_TO_SPORTS,
  DEFAULT_INDUSTRY_PROFILE_ID,
} from "../../lib/featureFlags";

/**
 * One question, four options. Each option maps to a (role, account_type) pair
 * so we don't ask the user twice.
 */
const SIGNUP_OPTIONS = [
  {
    id: "team",
    role: "coach",
    account_type: "team_coach",
    icon: Users2,
    title: "I manage a team",
    sub: "Soccer, baseball, basketball, football, and more.",
    summary: "Team coach",
  },
  {
    id: "train",
    role: "coach",
    account_type: "personal_trainer",
    icon: Dumbbell,
    title: "I train players",
    sub: "Private lessons, small groups, or skill training.",
    summary: "Personal trainer",
  },
  {
    id: "club",
    role: "coach",
    account_type: "organization",
    icon: Building2,
    title: "I run a club or organization",
    sub: "Academies, clubs, or multi-team programs.",
    summary: "Organization / club",
  },
  {
    id: "parent",
    role: "parent",
    account_type: "team_coach", // unused for parents (server ignores), keep schema valid
    icon: Heart,
    title: "I'm a parent or player",
    sub: "View fees, submit payment proof, and stay updated.",
    summary: "Parent or player",
  },
];

// Pull a code out of either a full invite URL or a raw code string. We accept
// both because a coach is likely to paste a link from text/WhatsApp/email.
const extractInviteCode = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  if (!s) return "";
  // Match /invite/{code} anywhere in the string (handles trailing query/hash).
  const m = s.match(/\/invite\/([A-Za-z0-9_-]+)/);
  if (m) return m[1];
  // Otherwise treat the trimmed string as a bare code.
  return s;
};

// Industry Profile cards shown to coaches on step 2 of signup. These
// map 1:1 with the backend's INDUSTRY_PROFILES catalog.
const INDUSTRY_OPTIONS = [
  { id: "team_sports",  title: "Team Sports",             sub: "Soccer, basketball, baseball, football, and more." },
  { id: "dance",        title: "Dance Studio",            sub: "Ballet, hip-hop, contemporary, and more." },
  { id: "martial_arts", title: "Martial Arts School",     sub: "Karate, taekwondo, BJJ, and more." },
  { id: "music",        title: "Music Program",           sub: "Bands, choirs, orchestras, and ensembles." },
  { id: "fitness",      title: "Fitness / Personal Training", sub: "Personal trainers, gyms, and group programs." },
  { id: "tutoring",     title: "Tutoring / Education",    sub: "Tutors and learning centers." },
  { id: "club",         title: "Club / Organization",     sub: "Clubs, nonprofits, and community groups." },
  { id: "other",        title: "Other",                   sub: "Something else — we'll use safe generic wording." },
];

export default function Signup() {
  const { register } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: role, 2: industry (coach only, MVP: skipped), 3: details / invite gate
  const [pickedId, setPickedId] = useState("team");
  const [industryId, setIndustryId] = useState(DEFAULT_INDUSTRY_PROFILE_ID);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Parent-only state — invite code / link gate
  const [inviteInput, setInviteInput] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [inviteChecking, setInviteChecking] = useState(false);
  const [showNoInviteHelp, setShowNoInviteHelp] = useState(false);

  const picked = SIGNUP_OPTIONS.find((o) => o.id === pickedId) || SIGNUP_OPTIONS[0];
  const isParentPath = picked.role === "parent";

  const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  // Coaches → step 2 (industry profile picker). Parents → step 3 (invite gate),
  // skipping the profile step which doesn't apply to them.
  //
  // MVP/beta launch: the multi-industry picker is intentionally hidden;
  // new coach accounts are set to `team_sports` by default. Setting
  // `MVP_INDUSTRY_LOCKED_TO_SPORTS = false` in `lib/featureFlags` restores
  // the picker without any backend change.
  const onContinueFromStep1 = () => {
    setError("");
    setInviteError("");
    setShowNoInviteHelp(false);
    if (isParentPath) { setStep(3); return; }
    if (MVP_INDUSTRY_LOCKED_TO_SPORTS) {
      // Ensure the default is in effect and skip straight to the details step.
      setIndustryId(DEFAULT_INDUSTRY_PROFILE_ID);
      setStep(3);
      return;
    }
    setStep(2);
  };

  const onContinueFromStep2 = () => {
    setError("");
    setStep(3);
  };

  // Parent submits an invite link / code. We validate it via GET /invites/{code}
  // (no auth required) and route to /invite/{code} where the standard accept
  // flow lives. If the code is invalid/expired we surface a clear message.
  const onParentInviteSubmit = async (e) => {
    e.preventDefault();
    const code = extractInviteCode(inviteInput);
    if (!code) {
      setInviteError("Please paste your invite link or invite code.");
      return;
    }
    setInviteError("");
    setInviteChecking(true);
    try {
      // Backend returns 404 if the invite is invalid, accepted, or expired.
      const base = (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";
      const r = await fetch(`${base}/api/invites/${encodeURIComponent(code)}`);
      if (!r.ok) {
        setInviteError("That invite link or code didn't work. Ask your coach to send a new one.");
      } else {
        navigate(`/invite/${code}`);
      }
    } catch {
      setInviteError("Couldn't reach Squad Fee. Check your connection and try again.");
    } finally {
      setInviteChecking(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await register({
        ...form,
        role: picked.role,
        account_type: picked.account_type,
        industry_profile: picked.role === "coach" ? industryId : undefined,
      });
      if (user.role === "coach") {
        // Loud, friendly nudge that a verification email is on the way. The
        // yellow banner inside AppLayout is the ongoing surface if they miss
        // the toast. Branch on `email_sent` so we don't lie when the mail
        // provider actually rejected the send — the banner + its Resend
        // button remain the recovery UI in either case.
        if (user.email_sent === false) {
          toast.warning("Account created — but we couldn't email you yet", {
            description: `Use the "Resend verification email" button on your dashboard to try again.`,
            duration: 9000,
          });
        } else {
          toast.success("Check your inbox to verify your email", {
            description: `We sent a link to ${user.email}. It expires in 24 hours.`,
            duration: 8000,
          });
        }
        navigate("/coach/dashboard");
      }
      else if (user.role === "parent") navigate("/parent/dashboard");
      else navigate("/");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not create account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-ink-100/40">
      <div className="m-auto w-full max-w-md px-6 py-10">
        <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold text-ink-900">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-white shadow-sm">
            <ShieldCheck className="h-5 w-5" />
          </span>
          {brand.app_name}
        </Link>

        <div className="mt-8 rounded-2xl border border-ink-100 bg-white p-8 shadow-sm">
          <h1 className="font-heading text-2xl font-bold text-ink-900">
            {step === 1
              ? "Create your account"
              : step === 2
              ? "What type of organization do you manage?"
              : isParentPath
              ? "Join your team"
              : brand.free_early_access
              ? "Create your free Early Access account"
              : "Start your 14-day free trial"}
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            {step === 1
              ? "Tell us how you'll use Squad Fee."
              : step === 2
              ? "We'll tailor Squad Fee to your program. You can change this anytime in Settings."
              : isParentPath
              ? "Parents and players join Squad Fee through an invite from their coach."
              : brand.free_early_access
              ? "Squad Fee is free during Early Access. No credit card required."
              : "No credit card required."}
          </p>

          {step === 1 ? (
            <>
              <p className="mt-6 text-sm font-semibold text-ink-900" data-testid="signup-question">
                What are you using {brand.app_name} for?
              </p>
              <div className="mt-3 space-y-2" role="radiogroup" aria-label="Signup type">
                {SIGNUP_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const active = pickedId === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setPickedId(opt.id)}
                      className={`flex w-full items-center gap-3 rounded-lg border p-4 text-left transition ${
                        active
                          ? "border-brand bg-brand-50 ring-1 ring-brand/20"
                          : "border-ink-200 bg-white hover:bg-ink-100"
                      }`}
                      data-testid={`signup-option-${opt.id}`}
                    >
                      <span
                        className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                          active ? "bg-brand text-white" : "bg-ink-100 text-ink-700"
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-ink-900">{opt.title}</p>
                        <p className="text-xs text-ink-500">{opt.sub}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <Button
                type="button"
                onClick={onContinueFromStep1}
                className="mt-6 h-12 w-full bg-brand font-semibold text-white hover:bg-brand-hover"
                data-testid="signup-continue"
              >
                Continue
              </Button>
              <p className="mt-3 text-center text-sm text-ink-700">
                Already have an account?{" "}
                <Link to="/login" className="font-semibold text-brand hover:underline" data-testid="signup-to-login">
                  Log in
                </Link>
              </p>
            </>
          ) : step === 2 ? (
            /* Industry Profile picker — coach only. Team Sports is preselected
               so the current MVP experience remains the default. */
            <>
              <div className="mt-6 space-y-2" role="radiogroup" aria-label="Industry profile">
                {INDUSTRY_OPTIONS.map((opt) => {
                  const active = industryId === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setIndustryId(opt.id)}
                      className={`flex w-full items-start gap-3 rounded-lg border p-4 text-left transition ${
                        active
                          ? "border-brand bg-brand-50 ring-1 ring-brand/20"
                          : "border-ink-200 bg-white hover:bg-ink-100"
                      }`}
                      data-testid={`signup-industry-${opt.id}`}
                    >
                      <span
                        className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                          active ? "border-brand bg-brand" : "border-ink-300"
                        }`}
                        aria-hidden="true"
                      >
                        {active ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-ink-900">{opt.title}</p>
                        <p className="text-xs text-ink-500">{opt.sub}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="h-12 flex-none px-4 font-semibold"
                  data-testid="signup-industry-back"
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={onContinueFromStep2}
                  className="h-12 flex-1 bg-brand font-semibold text-white hover:bg-brand-hover"
                  data-testid="signup-industry-continue"
                >
                  Continue
                </Button>
              </div>
            </>
          ) : isParentPath ? (
            /* Parent invite gate — parents must arrive through a coach invite */
            <div className="mt-6 space-y-4" data-testid="parent-invite-gate">
              <button
                type="button"
                onClick={() => { setStep(1); setShowNoInviteHelp(false); setInviteError(""); }}
                className="text-sm font-semibold text-ink-500 hover:text-ink-900"
                data-testid="signup-back"
              >
                ← Back
              </button>

              <p className="text-sm text-ink-700">
                To continue, enter the invite link or invite code your coach sent you.
              </p>

              <form onSubmit={onParentInviteSubmit} className="space-y-4" data-testid="parent-invite-form">
                <div>
                  <Label htmlFor="invite-input">Invite link or invite code</Label>
                  <Input
                    id="invite-input"
                    autoFocus
                    value={inviteInput}
                    onChange={(e) => { setInviteInput(e.target.value); setInviteError(""); }}
                    placeholder="https://squadfee.app/invite/… or code"
                    className="mt-1 h-12"
                    data-testid="parent-invite-input"
                  />
                </div>

                {inviteError ? (
                  <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="parent-invite-error">
                    {inviteError}
                  </p>
                ) : null}

                <Button
                  type="submit"
                  disabled={inviteChecking}
                  className="h-12 w-full bg-brand font-semibold text-white hover:bg-brand-hover"
                  data-testid="parent-invite-continue"
                >
                  {inviteChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
                </Button>
              </form>

              <button
                type="button"
                onClick={() => setShowNoInviteHelp((v) => !v)}
                className="block w-full text-center text-sm font-semibold text-brand hover:underline"
                data-testid="parent-no-invite-button"
              >
                I don&apos;t have an invite
              </button>

              {showNoInviteHelp ? (
                <p
                  className="rounded-lg border border-ink-200 bg-ink-100/60 px-3 py-3 text-sm text-ink-700"
                  data-testid="parent-no-invite-help"
                >
                  Ask your coach to send you an invite link. This connects you
                  to the correct player, team, and fees.
                </p>
              ) : null}
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="signup-form">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="text-sm font-semibold text-ink-500 hover:text-ink-900"
                data-testid="signup-back"
              >
                ← Back
              </button>
              <div className="rounded-lg bg-ink-100/60 px-3 py-2 text-xs text-ink-700" data-testid="signup-picked-summary">
                Signing up as <span className="font-semibold text-ink-900">{picked.summary}</span>
              </div>
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={form.name} onChange={update("name")} className="mt-1 h-12" data-testid="signup-name-input" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={form.email} onChange={update("email")} className="mt-1 h-12" data-testid="signup-email-input" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <PasswordInput id="password" required minLength={6} autoComplete="new-password" value={form.password} onChange={update("password")} className="mt-1 h-12" data-testid="signup-password-input" capsWarningTestId="signup-caps-warning" />
                <p className="mt-1 text-xs text-ink-500">Minimum 6 characters.</p>
              </div>

              {error ? (
                <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="signup-error">{error}</p>
              ) : null}

              <Button
                type="submit"
                disabled={loading}
                className="h-12 w-full bg-brand font-semibold text-white hover:bg-brand-hover"
                data-testid="signup-submit-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : brand.free_early_access ? "Create free account" : "Start 14-day free trial"}
              </Button>
              <p className="text-center text-xs leading-relaxed text-ink-500" data-testid="signup-legal-ack">
                By creating an account, you agree to the{" "}
                <Link to="/terms" target="_blank" rel="noopener" className="font-semibold text-brand hover:underline" data-testid="signup-terms-link">Terms of Service</Link>
                {" "}and acknowledge the{" "}
                <Link to="/privacy" target="_blank" rel="noopener" className="font-semibold text-brand hover:underline" data-testid="signup-privacy-link">Privacy Policy</Link>.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
